import { useEffect, useRef, useState } from 'react'

const LONG_PRESS_DELAY = 500
const LONG_PRESS_TOLERANCE = 10
const EDGE_SCROLL_ZONE = 60
const EDGE_SCROLL_STEP = 10
const NON_PRESSABLE_SELECTOR =
    'input, textarea, select, button, a, [contenteditable], .move-handle'

function getScrollY() {
    return window.pageYOffset || document.documentElement.scrollTop
}

// Reorder state for a list of items: move mode (started by startMove or a long
// press on an item), step/jump moves and pointer dragging by a handle.
// Each move calls onMove(from, to) once, so the caller can apply it as a single
// state update (see moveArrayItem in helpers).
export default function useMoveMode({ count, onMove }) {
    const [movingIndex, setMovingIndex] = useState(null)
    const [pressingIndex, setPressingIndex] = useState(null)
    const [drag, setDrag] = useState(null)
    const itemRefs = useRef([])
    const pressRef = useRef(null)
    const dragRef = useRef(null)
    const longPressedRef = useRef(false)

    const isActive = movingIndex !== null

    useEffect(
        function () {
            if (movingIndex !== null && movingIndex >= count) {
                setMovingIndex(null)
            }
        },
        [count],
    )

    useEffect(
        function () {
            if (movingIndex === null || dragRef.current) {
                return
            }
            itemRefs.current[movingIndex]?.scrollIntoView({
                block: 'nearest',
                behavior: 'smooth',
            })
        },
        [movingIndex],
    )

    useEffect(
        function () {
            if (!isActive) {
                return
            }

            function handleKeyDown(e) {
                if (e.key === 'Escape') {
                    setMovingIndex(null)
                }
            }

            document.addEventListener('keydown', handleKeyDown)
            return function () {
                document.removeEventListener('keydown', handleKeyDown)
            }
        },
        [isActive],
    )

    useEffect(function () {
        return function () {
            clearTimeout(pressRef.current?.timeout)
        }
    }, [])

    function startMove(index) {
        if (index < 0 || index >= count) {
            return
        }
        setMovingIndex(index)
    }

    function stopMove() {
        setMovingIndex(null)
    }

    function moveTo(to) {
        if (movingIndex === null) {
            return
        }
        const target = Math.max(0, Math.min(count - 1, to))
        if (target === movingIndex) {
            return
        }
        onMove(movingIndex, target)
        setMovingIndex(target)
    }

    function moveUp() {
        moveTo(movingIndex - 1)
    }

    function moveDown() {
        moveTo(movingIndex + 1)
    }

    function moveToTop() {
        moveTo(0)
    }

    function moveToBottom() {
        moveTo(count - 1)
    }

    function clearPress() {
        if (!pressRef.current) {
            return
        }
        clearTimeout(pressRef.current.timeout)
        pressRef.current = null
        setPressingIndex(null)
    }

    function handlePressStart(e, index) {
        longPressedRef.current = false
        if (e.button !== 0 || index === movingIndex) {
            return
        }
        if (e.target.closest(NON_PRESSABLE_SELECTOR)) {
            return
        }
        clearPress()
        pressRef.current = {
            x: e.clientX,
            y: e.clientY,
            timeout: setTimeout(function () {
                pressRef.current = null
                longPressedRef.current = true
                setPressingIndex(null)
                startMove(index)
            }, LONG_PRESS_DELAY),
        }
        setPressingIndex(index)
    }

    function handlePressMove(e) {
        if (!pressRef.current) {
            return
        }
        const dx = Math.abs(e.clientX - pressRef.current.x)
        const dy = Math.abs(e.clientY - pressRef.current.y)
        if (dx > LONG_PRESS_TOLERANCE || dy > LONG_PRESS_TOLERANCE) {
            clearPress()
        }
    }

    function handleContextMenu(e) {
        if (pressRef.current || longPressedRef.current) {
            e.preventDefault()
        }
    }

    function getDropIndex(clientY, from) {
        return itemRefs.current.slice(0, count).reduce(function (acc, el, i) {
            if (i === from || !el) {
                return acc
            }
            const rect = el.getBoundingClientRect()
            return rect.top + rect.height / 2 < clientY ? acc + 1 : acc
        }, 0)
    }

    function handleDragStart(e, index) {
        if (e.button !== 0) {
            return
        }
        e.preventDefault()
        e.currentTarget.setPointerCapture?.(e.pointerId)
        clearPress()
        dragRef.current = {
            from: index,
            to: index,
            startY: e.clientY,
            startScrollY: getScrollY(),
        }
        setMovingIndex(index)
        setDrag({ from: index, to: index, offsetY: 0 })
    }

    function handleDragMove(e) {
        if (!dragRef.current) {
            return
        }
        if (e.clientY < EDGE_SCROLL_ZONE) {
            window.scrollBy(0, -EDGE_SCROLL_STEP)
        } else if (e.clientY > window.innerHeight - EDGE_SCROLL_ZONE) {
            window.scrollBy(0, EDGE_SCROLL_STEP)
        }
        const { from, startY, startScrollY } = dragRef.current
        const to = getDropIndex(e.clientY, from)
        const offsetY = e.clientY + getScrollY() - startY - startScrollY
        dragRef.current.to = to
        setDrag({ from, to, offsetY })
    }

    function handleDragEnd() {
        if (!dragRef.current) {
            return
        }
        const { from, to } = dragRef.current
        dragRef.current = null
        setDrag(null)
        if (to !== from) {
            onMove(from, to)
            setMovingIndex(to)
        }
    }

    function handleDragCancel() {
        dragRef.current = null
        setDrag(null)
    }

    function getItemState(index) {
        const isDragTarget = drag && drag.to !== drag.from && drag.to === index
        return {
            isMoving: movingIndex === index,
            isPressing: pressingIndex === index,
            isDragging: drag?.from === index,
            isTargetBefore: isDragTarget && drag.to < drag.from,
            isTargetAfter: isDragTarget && drag.to > drag.from,
            offsetY: drag?.from === index ? drag.offsetY : 0,
        }
    }

    function getItemProps(index) {
        return {
            ref: function (el) {
                itemRefs.current[index] = el
            },
            onPointerDown: (e) => handlePressStart(e, index),
            onPointerMove: handlePressMove,
            onPointerUp: clearPress,
            onPointerLeave: clearPress,
            onPointerCancel: clearPress,
            onContextMenu: handleContextMenu,
        }
    }

    function getHandleProps(index) {
        return {
            onPointerDown: (e) => handleDragStart(e, index),
            onPointerMove: handleDragMove,
            onPointerUp: handleDragEnd,
            onPointerCancel: handleDragCancel,
            onContextMenu: (e) => e.preventDefault(),
        }
    }

    return {
        count,
        isActive,
        movingIndex,
        startMove,
        stopMove,
        moveUp,
        moveDown,
        moveToTop,
        moveToBottom,
        getItemState,
        getItemProps,
        getHandleProps,
    }
}
